import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSuperAdminOverview, SuperAdminOverview as Overview, SAObjectiveTile } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const FILL: Record<string, string> = { good: 'fill-good', warn: 'fill-warn', crit: 'fill-crit', neutral: 'fill-acc', dark: 'fill-acc' };
const TILE_MOD: Record<string, string> = { good: ' ok', warn: ' warn-t', crit: ' crit-t' };

const Tile: React.FC<{ t: SAObjectiveTile }> = ({ t }) => {
  const dark = t.state === 'dark' || t.value == null;
  const denom = t.total ?? t.target ?? 0;
  const width = t.unit === 'percent' ? t.value ?? 0 : denom ? Math.min(100, Math.round(((t.value ?? 0) / denom) * 100)) : 0;
  const notch = t.target != null && t.total != null ? Math.min(100, Math.round((t.target / t.total) * 100)) : null;
  return (
    <div className={`tile${TILE_MOD[t.state] || ''}${dark ? ' dark-ship' : ''}`}>
      <div className="lab">{t.label}</div>
      <div className="val">
        {dark ? '—' : t.unit === 'percent' ? `${t.value}%` : (
          <>{t.value}{t.total != null ? <small> / {t.total}</small> : null}</>
        )}
      </div>
      {t.note ? <div className="tgt">{t.note}</div> : null}
      {dark ? (
        <span className="capture">capture pending</span>
      ) : (
        <div className="meter">
          <i className={FILL[t.state] || 'fill-acc'} style={{ width: `${width}%` }} />
          {notch != null ? <span className="notch" style={{ left: `${notch}%` }} /> : null}
        </div>
      )}
    </div>
  );
};

/** Command Center (task 148), styled to the Mission Control artifact. */
const SuperAdminOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [week, setWeek] = useState(0);
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    setLoading(true);
    setError(null);
    getSuperAdminOverview(token, week)
      .then(setData)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, week]);

  return (
    <SuperAdminLayout>
      <div className="topbar">
        <div className="crumbs"><span className="cur">Overview</span></div>
        <div className="week">
          <b onClick={() => setWeek((w) => w - 1)}>‹</b>
          {data ? (data.is_current_week ? 'This week' : data.week_label) : '…'}
          <b className={week >= 0 ? 'dis' : ''} onClick={() => week < 0 && setWeek((w) => Math.min(0, w + 1))}>›</b>
        </div>
      </div>
      <div className="content">
        <h1 className="pg">Overview</h1>
        <p className="pg-sub">The week's objectives vs targets, and what needs attention — fit for action, not lifetime totals.</p>

        {loading ? (
          <p className="hint">Loading…</p>
        ) : error ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>
        ) : data ? (
          <>
            <div className="grid g4">
              {data.tiles.map((t) => <Tile key={t.key} t={t} />)}
            </div>

            <div className="card mt14">
              <h3>Needs intervention</h3>
              <p className="sub">Exceptions only — each tagged with the signal that flagged it.</p>
              {data.interventions.length === 0 ? (
                <p className="hint">Nothing flagged this week. ✓</p>
              ) : (
                <div className="ivl">
                  {data.interventions.map((it, i) => (
                    <div
                      key={i}
                      className="iv"
                      onClick={() => it.facilitator_id && navigate(`/super-admin/facilitators/${it.facilitator_id}`)}
                    >
                      <span className={`sig ${it.sig}`} />
                      <span className="what">
                        <b>{it.title}</b>
                        {it.detail ? <small>{it.detail}</small> : null}
                      </span>
                      <span className="modref">{it.module}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="hint">Windowed to the selected week. Assignments-completion ships once capture is live (task 146).</p>
          </>
        ) : null}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminOverviewPage;
